import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { DisplayDateFormat } from '@northfork/basics'
import { Subject, Subscription } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators'
import {
  HttpDialogListValuesService,
  ResolvedLists,
} from '../../services/http-dialog-list-values.service'
import {
  derivationRequest,
  derivationTriggers,
  FieldDerivation,
} from '../../edit-transaction/derivation'
import {
  collectionRequiredUnsatisfied,
  DerivationState,
  deriveFieldStatus,
  EditTransactionCollection,
  EditTransactionComparisonSource,
  EditTransactionField,
  EditTransactionFieldStatus,
  EditTransactionViewModel,
  requiredRuleUnmet,
  requiredUnsatisfied,
} from '../../edit-transaction/edit-transaction.projection'
import {
  editValue,
  formatFieldValue,
  rawValueFrom,
} from '../../edit-transaction/format-field-value'
import {
  BlankCellReporter,
  blankCellLog,
  LOOKUP_DEBOUNCE_MS,
  normalizeOptions,
} from '../../edit-transaction/list-options'
import { API_SOURCE } from '../../models/source-key.model'
import { TRANSACTION_SAVE_COPY } from '../../models/transaction-action.model'
import { TransactionSaveRequest } from '../../models/transaction-save.model'
import {
  assembleCreateRecord,
  assembleEditRecord,
  editBaseline,
  SaveAssemblyError,
  SaveBaseline,
} from '../../edit-transaction/transaction-save.assembly'
import { saveCategory } from '../../edit-transaction/transaction-category-wire'
import { saveFailureMessage } from '../../services/http-transaction-save.service'
import { TransactionSaveService } from '../../services/transaction-save.service'
import {
  DerivationRequest,
  DerivationResult,
  TransactionDerivationSource,
} from '../../services/transaction-derivation.service'
import {
  CollectionItemsModalComponent,
  CollectionItemsResult,
  lineOriginOf,
} from '../collection-items-modal/collection-items-modal.component'
import {
  pickerLabel,
  ValuePicker,
  ValuePickerOption,
} from '../value-picker/value-picker'
import {
  buildRows,
  controlFor,
  EditTransactionControl,
  EditTransactionRow,
  STATUS_META,
} from './edit-transaction-grid'

/** A raw value as the commit check compares it — `40554` and `'40554'` are the same commit. */
const committedKey = (value: unknown): string =>
  value == null ? '' : String(value).trim()

/** One pending lookup search: what was typed, for which field, under which cell. */
interface LookupRequest {
  field: EditTransactionField
  query: string
  anchor: HTMLElement
}

/** One tally in the summary bar above the fields. */
export interface StatusSummaryEntry {
  status: EditTransactionFieldStatus
  count: number
  icon: string
  tone: string
  label: string
}

/**
 * The metadata-driven Create/Edit Transaction modal (PVAL-26341, Phase 1).
 *
 * Deliberately dumb: it renders the {@link EditTransactionViewModel} the host projected and reports
 * clicks. It holds no metadata, no fixtures, no row and no service — everything it shows was decided
 * by `projectEditTransaction`, which is why the whole layout is testable without a TestBed.
 *
 * The field list is HTML, not a data grid. It looks like a grid because the columns are fixed-width
 * and every row packs them to the left, but each row carries its own control and its own container —
 * that is a form, and a form is what the metadata layer describes. A collection is summarised as one
 * of those rows and edited as repeating rows in its own modal; no part of this dialog is a data grid.
 *
 * Opened as an `NgbModal`, following the `unmatch-modal` precedent: the host opens it and assigns
 * `vm` on the component instance.
 *
 * **Save is real (PVAL-26440).** It assembles the record (`transaction-save.assembly.ts`) and sends
 * ONE `POST transactions/save` through {@link TransactionSaveService}. Success creates a pending
 * command in the commands drawer — nothing is written to Enfusion here — and closes the dialog. A
 * rejection keeps it open and editable with the reason shown. It never mutates the workbench row.
 *
 * Both halves of the Req column are judged live, through the projection's
 * {@link requiredUnsatisfied} — a blank required field and a value short of its minimum word count
 * are both things the dialog can see for itself, so both are counted in the footer note and both
 * disable Save. What it does NOT judge is whether a value that IS there is legal; see {@link onEdit}
 * for why the rest of validation waits for a backend.
 */
@Component({
  selector: 'hs-edit-transaction-modal',
  templateUrl: './edit-transaction-modal.component.html',
  styleUrls: ['./edit-transaction-modal.component.scss'],
})
export class EditTransactionModalComponent implements OnDestroy {
  private model: EditTransactionViewModel | null = null

  /**
   * True while the save request is in flight (PVAL-26440). The overlay covers the fields, Save /
   * Cancel / ✕ are disabled, and the host's `beforeDismiss` refuses ESC and the backdrop — the
   * request is not idempotent, so nothing may let a second one start.
   */
  saving = false

  /** Why the last Save did not go through, or why it was not sent at all. Null when nothing failed. */
  saveError: string | null = null

  /** What an edit is diffed against — taken on every `set vm`, before derived answers re-apply. */
  private baseline: SaveBaseline | null = null

  /** What the overlay says while the save is in flight. */
  readonly savingLabel = 'Saving…'

  /**
   * True while the whole trade is being read from `trades/get` (PVAL-26492).
   *
   * The dialog opens immediately on the values the grid already has and the fuller document lands
   * when it arrives, so without this the fields visibly rewrite themselves a moment after opening
   * with nothing to say why. Set by the host, which owns the fetch.
   */
  loading = false

  /**
   * True while the edited row's server-matched counterparts are being read from
   * `transactions/matches` (PVAL-26451).
   *
   * The dialog opens at once rather than waiting on that read, so until it lands the Custody/Trading
   * comparison columns and the status dots are scored against nothing. The overlay covers them until
   * they are real. Set by the host, which owns the fetch.
   */
  matchesLoading = false

  /**
   * The failed trade read, shown in `hs-dialog-error-details` below the fields — the same error box
   * the Create/Edit Transaction modal puts below Notes, and the same two inputs.
   *
   * Set by the host alongside {@link loading}. A failure does not blank the dialog: the
   * grid-projected values stay, and this says why the fuller ones did not arrive.
   */
  errorHeader: string | undefined
  errorDetails: unknown

  /**
   * The message shown ON the frozen dialog when the read failed.
   *
   * Set means frozen: `hs-loader` swaps its spinner for an error icon and this text, and the
   * `active` class below flips its `pointer-events` on so the overlay blocks the fields behind it.
   * That is the Create/Edit Transaction modal's own rule — a load that failed leaves the overlay up
   * rather than revealing a half-filled form the user could edit and try to save.
   */
  errorLabel: string | undefined

  /** Either read is still in flight. */
  get busy(): boolean {
    return this.loading || this.matchesLoading
  }

  /**
   * What the overlay says while a read is in flight. The matches read is named whenever it is
   * outstanding, since it is the one the comparison columns are waiting on.
   */
  get loadingLabel(): string {
    return this.matchesLoading ? 'Loading Matches…' : 'Loading transaction…'
  }

  /**
   * Whether the overlay is covering the dialog — still reading, or stopped on an error.
   *
   * One getter for both, because the overlay's behaviour is identical in each: it is visible and it
   * swallows clicks. Only its contents differ, and `hs-loader` decides that from `errorLabel`.
   */
  get frozen(): boolean {
    return this.busy || this.saving || !!this.errorLabel
  }

  /** Header for the edited-values column, from the DOCUMENT's own `source` key. */
  get valueColumnLabel(): string {
    return this.model?.valueLabel ?? API_SOURCE.name
  }

  /** Section headings and field rows, in render order. */
  rows: EditTransactionRow[] = []

  /**
   * Hides comparison columns that said nothing for this record. On by default, per the design: a
   * record is usually matched against one source, and three mostly-empty columns push the values
   * you came to read off the side of the dialog.
   *
   * A viewing preference, not a fact — unchecking shows Prometheus Custody, Prometheus Trading and
   * Clearwater whether or not they answered.
   */
  hideAbsentSources = true

  /** The comparison columns actually rendered, honouring the switch. */
  get visibleSources(): EditTransactionComparisonSource[] {
    const sources = this.model?.comparisonSources ?? []
    return this.hideAbsentSources
      ? sources.filter(source => !source.absent)
      : sources
  }

  /** True when some source is being hidden, so the switch can say how many. */
  get absentSourceCount(): number {
    return (this.model?.comparisonSources ?? []).filter(s => s.absent).length
  }

  /**
   * Trigger field -> the fields whose derivations name it.
   *
   * Rebuilt with the view model rather than per keystroke. It is the dependency graph read by
   * inverting the derivations, which is why the metadata needs no trigger column.
   */
  private triggers = new Map<string, string[]>()

  /** The open value panel, or the absence of one. */
  readonly picker = new ValuePicker()

  /** The field the open panel belongs to. */
  private pickerField: EditTransactionField | null = null

  /** The field currently being edited, by reference. */
  private armed: EditTransactionField | null = null

  /** `MM/dd/yyyy` — the app-wide display format, and what `dateUS` already writes. */
  readonly dateDisplayFormat = DisplayDateFormat

  /** Keyed by field REFERENCE, like {@link armed}: two fields can share a label. */
  private readonly dateControls = new Map<EditTransactionField, FormControl>()

  private dateBindings = new Subscription()

  /** Set while a control's own edit is being written back, so {@link write} does not echo it
   * into the control mid-keystroke — an incomplete `03/0` parses to nothing, and pushing that
   * back would clear the box the user is still typing in. */
  private writingFromDateControl = false

  // ── Derivation state (PVAL-26685) ──────────────────────────────────────────────────────────

  /** What each derivation answered, re-applied over every re-projection (trade prefill, catalogs). */
  private readonly derivedValues = new Map<string, unknown>()

  /** Each field's derivation state, carried across re-projections like {@link derivedValues}. */
  private readonly derivationStates = new Map<string, DerivationState>()

  /**
   * The latest request per field. A reply is applied only if it answers the newest request for its
   * field — a commit made while an older one is in flight discards the older reply, field by field.
   */
  private readonly derivationSeq = new Map<string, number>()
  private nextSeq = 0

  /** The requests the host's opening call carries, by field, so its reply is judged by the same rule. */
  private openingSeqs = new Map<string, number>()

  /** The last value committed per field, so a commit that changed nothing re-derives nothing. */
  private readonly committed = new Map<string, string>()

  private readonly derivationCalls = new Subscription()

  /** The dialog's once-per-(field, column) log for lookup rows missing a display column. */
  private readonly blankCells = blankCellLog()

  constructor(
    readonly activeModal: NgbActiveModal,
    private readonly modal: NgbModal,
    private readonly derivations: TransactionDerivationSource,
    private readonly host: ElementRef<HTMLElement>,
    private readonly listValues: HttpDialogListValuesService,
    private readonly injector: Injector,
    private readonly saveService: TransactionSaveService,
  ) {
    this.lookups.add(
      this.typed
        .pipe(
          // Per keystroke would be one request per character. The pause is the user stopping,
          // which is the only moment the text is worth asking about.
          debounceTime(LOOKUP_DEBOUNCE_MS),
          distinctUntilChanged(
            (before, after) =>
              before.field === after.field && before.query === after.query,
          ),
          // `switchMap`, so a reply for text the user has already moved past is DROPPED rather
          // than racing the newer one into the panel. Only one panel is ever open, so
          // cancelling across fields is the same correctness.
          switchMap(request =>
            this.listValues
              .search(request.field.field, request.query, this.accountId)
              .pipe(map(result => ({ request, result }))),
          ),
        )
        .subscribe(({ request, result }) =>
          this.onLookupResult(request, result),
        ),
    )
  }

  // ── Lookups ──────────────────────────────────────────────────────────────────
  // A `search` field is a text box that asks the resolve endpoint what the typed text matches
  // and offers the answers in the same anchored panel an enum uses. It is NOT an enum: the set
  // is unbounded, so there is nothing to show until something is typed, and the box stays
  // typeable rather than becoming a trigger.

  /** Typed text, before debouncing. */
  private readonly typed = new Subject<LookupRequest>()

  private readonly lookups = new Subscription()

  /** The field whose search is in flight, so the box can say so. */
  searching: EditTransactionField | null = null

  /** The field whose last search found nothing, so the box can say THAT instead. */
  noMatches: EditTransactionField | null = null

  /**
   * What a lookup's box shows (PVAL-26685): the `text` of the loaded option its value matches, else
   * the value itself. One rule for every lookup, read off the field's normalized options — there is
   * no second label store to fall out of step with the value.
   */
  lookupText(field: EditTransactionField): string {
    return pickerLabel(field.listValues ?? [], field.value)
  }

  /** Whether this field searches at all. A `lookup` with no `search` list is plain free text. */
  searchable(field: EditTransactionField): boolean {
    return field.minChars != null
  }

  /**
   * A keystroke in a lookup box.
   *
   * The text is written to the field FIRST and unconditionally — a lookup is still a text box,
   * and a field whose endpoint never answers must still keep what was typed into it. The search
   * is the extra, not the point.
   */
  onLookupInput(
    field: EditTransactionField,
    text: string,
    anchor: HTMLElement,
  ): void {
    this.onEdit(field, text)
    if (!this.searchable(field)) return

    const query = text.trim()
    this.noMatches = null
    if (query.length < (field.minChars ?? 0)) {
      // Below the threshold there is nothing to ask and nothing to show. The panel shuts rather
      // than keeping the previous query's answers over text that no longer produced them.
      this.searching = null
      if (this.picker.isOpen(field.field)) this.picker.close()
      return
    }
    this.searching = field
    this.typed.next({ field, query, anchor })
  }

  private onLookupResult(request: LookupRequest, result: ResolvedLists): void {
    const { field, anchor } = request
    this.searching = null

    const failure = result.error ?? result.failures.get(field.field)
    if (failure != null) {
      // Logged, not shown: a lookup that cannot search is still a usable text box, and freezing
      // the dialog over it would cost the user the whole form for one field.
      console.error(
        `[transactions-2] lookup search failed for ${field.field}; the box stays free text`,
        failure,
      )
      this.noMatches = null
      if (this.picker.isOpen(field.field)) this.picker.close()
      return
    }

    const rows = result.options.get(field.field) ?? []
    // Read by the field's own response keys, exactly as a catalog's rows were at projection time.
    const options = field.listKeys
      ? normalizeOptions(
          rows,
          field.listKeys,
          this.blankCellReporter(field.field),
        )
      : []
    if (!options.length) {
      this.noMatches = field
      if (this.picker.isOpen(field.field)) this.picker.close()
      return
    }

    this.noMatches = null
    // Kept on the FIELD as well, so the box can show the picked row's `text` without this method
    // having to hand the panel a second channel.
    field.listValues = options
    this.pickerField = field
    this.picker.show(
      field.field,
      options,
      anchor,
      this.host.nativeElement,
      field.displayColumns,
    )
  }

  ngOnDestroy(): void {
    // An open panel holds window scroll/resize listeners that only `close` removes; left attached they
    // would keep the panel, its anchor and this dialog's element alive after the dialog is gone.
    this.picker.close()
    this.dateBindings.unsubscribe()
    this.lookups.unsubscribe()
    // A derivation reply that lands after the dialog closed has nowhere to go.
    this.derivationCalls.unsubscribe()
  }

  // ── Dates ──────────────────────────────────────────────────────────────────────────────────
  // A date cell is the app's `nf-datepicker`, which every other helios dialog uses. It is a
  // ControlValueAccessor with no value INPUT: its inner `<input ngbDatepicker>` binds
  // `[formControl]` to the host's own `NgControl.control`, so the control it is bound to is the
  // only channel in either direction. Hence a real `FormControl` per date field rather than the
  // `[value]` + `(input)` the other controls use.
  //
  // `(dateSelect)` is deliberately NOT used: it fires only for a calendar pick, so a TYPED date
  // would never reach `onEdit`. The component's own docstring says to prefer the control.

  /** The control a date cell binds to. Stable by reference: a new one per change detection would
   * re-bind the input on every tick. */
  dateControl(field: EditTransactionField): FormControl {
    const existing = this.dateControls.get(field)
    if (existing) return existing
    return this.bindDateControl(field)
  }

  private bindDateControl(field: EditTransactionField): FormControl {
    // The ISO form, not the displayed one: `nf-datepicker` parses the model and formats it
    // itself, and ISO is what `dateUS` stores and what the API speaks.
    const control = new FormControl(editValue(field.rawValue, field.style))
    this.dateBindings.add(
      control.valueChanges.subscribe(value => {
        this.writingFromDateControl = true
        try {
          this.onEdit(field, value ?? '')
          // Committed on the spot rather than on blur, exactly as a picked enum value is: the
          // control owns its own display, so there is no edit form to hold open.
          this.onCommit(field)
        } finally {
          this.writingFromDateControl = false
        }
      }),
    )
    this.dateControls.set(field, control)
    return control
  }

  /** Dropped with the model the controls belonged to — `set vm` runs again when the trade read
   * lands, and the old fields are gone. */
  private resetDateControls(): void {
    this.dateBindings.unsubscribe()
    this.dateBindings = new Subscription()
    this.dateControls.clear()
  }

  /** Dismissal for the value panel. */
  @HostListener('document:mousedown', ['$event.target'])
  onDocumentMouseDown(target: EventTarget | null): void {
    this.picker.dismiss(target)
  }

  /** The values a field's panel offers. `listValues` is the document's own pair list. */
  private pickerOptionsFor(field: EditTransactionField): ValuePickerOption[] {
    return field.listValues ?? []
  }

  /** What an enum cell reads at rest: the LABEL of the value it holds, not the value. */
  pickerText(field: EditTransactionField): string {
    return pickerLabel(this.pickerOptionsFor(field), field.value)
  }

  /** Open this field's panel, or shut it if it is the one already open. */
  openPicker(
    field: EditTransactionField,
    anchor: HTMLElement,
    event: Event,
  ): void {
    event.preventDefault()
    this.picker.toggle(
      field.field,
      this.pickerOptionsFor(field),
      field.value,
      anchor,
      this.host.nativeElement,
      field.displayColumns,
    )
    this.pickerField = this.picker.open ? field : null
  }

  /** A value pressed in the panel, written through the same path a typed edit takes. */
  pickValue(value: string): void {
    const field = this.pickerField
    this.picker.close()
    this.pickerField = null
    if (!field) return
    this.onEdit(field, value)
    this.onCommit(field)
  }

  /** Assigned by the host immediately after `NgbModal.open` — a plain property, not an `@Input`,
   * because the modal is opened imperatively and never appears in a template.
   *
   * PVAL-26685: assigning a view model RUNS NOTHING. The host re-projects on every fill (open, the
   * catalog resolve, the trade read), and a derivation fired from here would cost one `/resolve`
   * call per re-render. The opening derivations travel in the host's one opening call instead
   * ({@link openingDerivationRequests} / {@link applyDerivationResults}); what they answered is
   * re-applied to each new model, so a later re-projection cannot wipe it. */
  set vm(model: EditTransactionViewModel | null) {
    this.model = model
    this.saveError = null
    this.rows = model && !model.blocked ? buildRows(model) : []
    this.triggers = derivationTriggers(model?.derivations ?? [])
    this.resetDateControls()
    this.committed.clear()
    // PVAL-26440: what the record holds, BEFORE the derived answers below are written over it — so a
    // derivation that changes a value is sent as the change it is. Retaken on every re-projection
    // (the trade read lands after open), so a prefilled field never reads as an edit.
    this.baseline =
      model && !model.blocked && model.mode === 'edit'
        ? editBaseline(model)
        : null
    if (!model || model.blocked) return

    this.derivedValues.forEach((value, name) => {
      const target = this.fieldNamed(name)
      if (!target) return
      this.write(target, value)
      this.settle(target)
    })
    this.derivationStates.forEach((state, name) => {
      const target = this.fieldNamed(name)
      if (target) target.derivationState = state
    })
    // What each field holds now is its committed value: only a change FROM this re-derives.
    this.allFields().forEach(field =>
      this.committed.set(field.field, committedKey(field.rawValue)),
    )
  }

  get vm(): EditTransactionViewModel | null {
    return this.model
  }

  /** Which control a field edits with. Read from metadata, never from the field's name. */
  control(field: EditTransactionField): EditTransactionControl {
    return controlFor(field)
  }

  /**
   * The collection a row summarises, or null for an ordinary field.
   *
   * A summary is a row of the dialog in its own display group — "Distribution Lines" sits in
   * "Journal entry" at order 25, not in a block after the field list — so the row is found here by
   * name rather than rendered from a separate list.
   */
  collectionFor(field: EditTransactionField): EditTransactionCollection | null {
    return (
      this.model?.collections.find(entry => entry.name === field.field) ?? null
    )
  }

  /**
   * Every field the dialog shows, in one list — the rows (collection summaries among them) and the
   * footer fields. This is the population both the status tallies and the required-count report on,
   * and it is read live rather than from the projection's opening snapshot so both move as the user
   * types.
   */
  private allFields(): EditTransactionField[] {
    const model = this.model
    if (!model || model.blocked) return []
    return [
      // Collection summaries are section rows now, so they are already in the first list — adding
      // `collections` here again would count every one of them twice.
      ...model.sections.flatMap(section => section.fields),
      ...model.footer,
    ]
  }

  get missingRequiredCount(): number {
    return (
      this.allFields().filter(requiredUnsatisfied).length +
      this.collectionShortfall()
    )
  }

  /**
   * Blank required cells across every collection's lines, read live off the projected rows — which
   * {@link applyCollection} rewrites when the items modal applies an edit, so filling a line's
   * account clears it from the note without reprojecting.
   */
  private collectionShortfall(): number {
    if (!this.model || this.model.blocked) return 0
    return this.model.collections.reduce(
      (total, collection) => total + collectionRequiredUnsatisfied(collection),
      0,
    )
  }

  /**
   * Whether Save is refused right now — a live re-read of the same verdict the projection judged at
   * open, over the values as they stand.
   *
   * Both halves of Req decide this, because the dialog can answer both without a backend: a required
   * field left blank ("Req — required to save") and a supplied value that breaks the minimum word
   * count the row itself is displaying. Blank required cells on a collection's lines count too — the
   * items modal marks them, so ignoring them here would let the dialog contradict its own grid.
   *
   * What is still NOT judged is whether a value that IS there is legal — that needs the save
   * endpoint, and until it exists a filled field is accepted as answered (see {@link onEdit}).
   */
  get saveBlocked(): boolean {
    if (!this.model || this.model.blocked) return true
    return (
      this.allFields().some(requiredUnsatisfied) ||
      this.collectionShortfall() > 0
    )
  }

  /** The summary bar's tallies, in the order the design lists them. Zero counts still show, so the
   * bar does not reflow as a draft is filled in. */
  get statusSummary(): StatusSummaryEntry[] {
    if (!this.model || this.model.blocked) return []
    const counts: Record<EditTransactionFieldStatus, number> = {
      match: 0,
      diff: 0,
      partial: 0,
      novalue: 0,
    }
    this.allFields().forEach(field => {
      counts[field.status] += 1
    })
    const order: EditTransactionFieldStatus[] = [
      'match',
      'diff',
      'partial',
      'novalue',
    ]
    return order.map(status => ({
      status,
      count: counts[status],
      ...STATUS_META[status],
    }))
  }

  /**
   * Whether this row's rendered Req Parameter hint is currently broken by the value beside it.
   *
   * The template asks per row so the hint that STATES the rule is also the thing that shows it is
   * unmet — the same predicate {@link saveBlocked} and the footer count read, so the row, the count
   * and the button always agree.
   */
  requiredRuleBroken(field: EditTransactionField): boolean {
    return requiredRuleUnmet(field)
  }

  /**
   * Whether this field's required marker should read as UNANSWERED right now.
   *
   * The template extends the asterisk that already marks the field required rather than drawing a
   * second glyph beside it: "required" and "required and not answered yet" are two states of one
   * marker, and saying the same thing twice in two shapes is what makes a dense form unreadable.
   * Both the grid row and the footer field use it, so a field curated into the footer is marked the
   * same way it would have been in the grid.
   *
   * Read off the same {@link requiredUnsatisfied} the footer count and the Save button read, so the
   * marker cannot claim a field is answered while the button refuses to save it.
   */
  requiredUnanswered(field: EditTransactionField): boolean {
    return requiredUnsatisfied(field)
  }

  /** Icon + tone for one field's status dot. */
  statusMeta(field: EditTransactionField): {
    icon: string
    tone: string
    label: string
  } {
    return STATUS_META[field.status]
  }

  /**
   * Open a collection's lines in their own modal, over this one.
   *
   * The lines come back as rows of formatted strings and are written straight onto the projected
   * collection — display-only, like every other edit here — and the summary row above them is then
   * re-run by the projection's own aggregation ({@link EditTransactionCollection.summarise}), so a
   * `sum` summary reads the new total rather than the one the dialog opened with. The parse of the
   * display cells is the projection's and is confined to numeric columns; the figure is display-only
   * until the save-side work, where the raw values live, replaces it.
   */
  openCollection(collection: EditTransactionCollection): void {
    const ref = this.modal.open(CollectionItemsModalComponent, {
      // Handed over for the reason the grid hands one to THIS dialog: `NgbModal.open` builds from
      // the MODULE injector, so the panel's providers — an element injector — are invisible to it.
      // The items modal injects `HttpDialogListValuesService` for its lookup columns, and without
      // this it dies on open with `R3InjectorError(AppModule)[HttpDialogListValuesService]`.
      injector: this.injector,
      // Sized to its own columns rather than to a Bootstrap size, so every column of a line is
      // visible at once without a horizontal scrollbar.
      // `hs-modal-window` FIRST, and never alone: the app sets it as the global default
      // windowClass (app.module.ts) and it carries `pointer-events: none`, which is what lets
      // the workbench behind a dialog still take a click — every legacy dialog relies on it.
      // Passing a windowClass REPLACES that default rather than adding to it, so a dialog
      // naming its own was silently swallowing every click on the page behind it.
      windowClass: 'hs-modal-window ci-modal-window',
      centered: true,
      scrollable: true,
    })
    const instance = ref.componentInstance as CollectionItemsModalComponent
    instance.init(collection, { accountId: this.accountId })
    ref.result.then(
      (result: CollectionItemsResult) => this.applyCollection(result),
      () => undefined,
    )
  }

  private applyCollection(result: CollectionItemsResult): void {
    const collection = this.model?.collections.find(
      entry => entry.name === result.name,
    )
    if (!collection) return
    collection.rows = result.rows.map(row =>
      collection.columns.map(column => row[column.field] ?? ''),
    )
    // Which original line each one is (PVAL-26440), read back off the hidden key the items modal
    // carried through the edit — so a removal above a line cannot hand it another line's raw keys.
    collection.lineOrigins = result.rows.map(lineOriginOf)
    this.resummarise(collection)
  }

  /** The collection's summary row, re-run over its edited lines. */
  private resummarise(collection: EditTransactionCollection): void {
    const summary = this.fieldNamed(collection.name)
    if (!summary || !collection.summarise) return
    const { rawValue, value } = collection.summarise(collection.rows)
    summary.rawValue = rawValue
    summary.value = value
    summary.status = deriveFieldStatus(summary)
  }

  /**
   * Local edits are display-only; the VM is the modal's own copy, never the workbench row.
   *
   * TODO(PVAL-26341): this is where field validation belongs. Changing a value should call the
   * backend to have that value judged — legal instrument, account that exists, date inside the
   * period, and so on — and the answer should drive the row's state and gate Save. That endpoint
   * does not exist yet (it lands with the edit-transaction backend framework, alongside GET Trade /
   * GET GL Entry), so nothing is called here.
   *
   * Until then the row re-derives its COMPARISON status locally, which is the honest thing this can
   * answer offline: an empty required field stops reading as "no value" the moment it is filled, and
   * a value that now matches its counterpart turns green. The field's Req verdict is re-judged from
   * the same edit (see {@link saveBlocked}) for the same reason — whether a box is empty, and how
   * many words are in it, are both answerable from the string alone. What this deliberately does NOT
   * do is claim the value is otherwise *valid*: no local rule stands in for the validation call
   * above.
   */
  onEdit(field: EditTransactionField, value: string): void {
    this.write(field, value)
    // A value the user typed over a derived one is theirs now; a re-projection must not put the
    // derived value back. Derivations do NOT fire here — per keystroke would be one `/resolve`
    // call per character. They fire when the edit is committed (see `onCommit`).
    this.derivedValues.delete(field.field)
  }

  // ── Derivation ─────────────────────────────────────────────────────────────────────────────
  // A derivation is authored on the field that RECEIVES the value, so the wiring here only has to
  // answer "what was committed, and what named it". The rules — which inputs, when a request can be
  // made, the dependency graph — live in `derivation.ts` and are tested without a component; the
  // call itself is `TransactionDerivationSource.resolve` (PVAL-26685).

  /** The account the dialog was opened for — what every `/resolve` call names. */
  get accountId(): string {
    const account = this.model?.context['accountId']
    return account == null ? '' : String(account)
  }

  /** Handed to the projection by the host, so catalog rows report into the same log. */
  blankCellReporter(field: string): BlankCellReporter {
    return this.blankCells(field)
  }

  /**
   * The `context` derivations' items for the host's opening `/resolve` call — marked pending here,
   * answered through {@link applyDerivationResults}. A context derivation whose input is unset is
   * left out: there is nothing to ask.
   */
  openingDerivationRequests(): DerivationRequest[] {
    const context = (this.model?.derivations ?? []).filter(
      entry => entry.from === 'context',
    )
    const { items, seqs } = this.beginDerivations(context)
    this.openingSeqs = seqs
    return items
  }

  /** The opening call's derivation results, as the host received them. */
  applyDerivationResults(results: readonly DerivationResult[]): void {
    const seqs = this.openingSeqs
    this.openingSeqs = new Map()
    this.applyResults(results, seqs, new Set())
  }

  /** Re-send one field's derivation — the failed indicator's retry. */
  retryDerivation(field: EditTransactionField): void {
    const derivation = this.model?.derivations.find(
      entry => entry.field === field.field,
    )
    if (derivation) this.runDerivations([derivation])
  }

  /** Writes a value onto a field and re-judges its comparison status. */
  private write(field: EditTransactionField, value: unknown): void {
    const raw = rawValueFrom(value, field.style)
    field.rawValue = raw
    // A date field's control is the only thing that renders it, so a value arriving from
    // anywhere ELSE — a derivation, most of all — has to be pushed into it. Silently: the
    // control's own `valueChanges` is what called this.
    if (!this.writingFromDateControl) {
      this.dateControls
        .get(field)
        ?.setValue(editValue(raw, field.style), { emitEvent: false })
    }
    field.value = value == null ? '' : String(value)
    field.status = deriveFieldStatus({
      ...field,
      value: formatFieldValue(raw, field.style),
    })
  }

  /** Whether this field is the one being edited. Reference identity: two fields can share a label. */
  isArmed(field: EditTransactionField): boolean {
    return this.armed === field
  }

  onArm(field: EditTransactionField): void {
    this.armed = field
    field.value = editValue(field.rawValue, field.style)
  }

  onCommit(field: EditTransactionField): void {
    if (this.armed === field) this.armed = null
    this.settle(field)
    // A COMMITTED change, not a keystroke, is what re-derives — and only a real change: tabbing
    // through a field without altering it asks nothing.
    const now = committedKey(field.rawValue)
    if (this.committed.get(field.field) === now) return
    this.committed.set(field.field, now)
    this.deriveDependentsOf(field.field)
  }

  /** A field's display, re-read from its raw value. */
  private settle(field: EditTransactionField): void {
    field.value = formatFieldValue(field.rawValue, field.style)
    field.status = deriveFieldStatus(field)
  }

  private fieldNamed(name: string): EditTransactionField | undefined {
    return this.allFields().find(entry => entry.field === name)
  }

  private setDerivationState(name: string, state: DerivationState): void {
    if (state === 'idle') this.derivationStates.delete(name)
    else this.derivationStates.set(name, state)
    const target = this.fieldNamed(name)
    if (target) target.derivationState = state
  }

  /**
   * Re-derive every field whose inputs name `changed`.
   *
   * `seen` breaks a cycle: a derived field can itself be a trigger, so writing it cascades. Nothing
   * in today's metadata chains, and RPS refuses a cycle (V13), but a graph read from served config
   * should not be able to hang the dialog.
   */
  private deriveDependentsOf(changed: string, seen = new Set<string>()): void {
    const dependents = this.triggers.get(changed)
    if (!dependents?.length) return

    const pending = (this.model?.derivations ?? []).filter(
      entry => dependents.includes(entry.field) && !seen.has(entry.field),
    )
    pending.forEach(entry => seen.add(entry.field))
    this.runDerivations(pending, seen)
  }

  /**
   * Build the items for a set of derivations and stamp each field's newest request.
   *
   * A derivation whose inputs are not all set asks nothing — and still takes a new stamp, so a
   * reply to an older request (made while the input was filled) cannot land over a field whose
   * trigger has since been cleared.
   */
  private beginDerivations(derivations: readonly FieldDerivation[]): {
    items: DerivationRequest[]
    seqs: Map<string, number>
  } {
    const items: DerivationRequest[] = []
    const seqs = new Map<string, number>()
    for (const derivation of derivations) {
      const seq = ++this.nextSeq
      this.derivationSeq.set(derivation.field, seq)
      const request = derivationRequest(
        derivation,
        name => this.fieldNamed(name)?.rawValue,
        key => this.model?.context[key],
      )
      if (!request) {
        this.setDerivationState(derivation.field, 'idle')
        continue
      }
      items.push({ keyName: request.keyName, params: request.params })
      seqs.set(request.field, seq)
      this.setDerivationState(request.field, 'pending')
    }
    return { items, seqs }
  }

  /**
   * Ask `/resolve` for a set of derivations — ONE call for the lot, so committing Instrument ID
   * costs one request carrying both currencies.
   */
  private runDerivations(
    derivations: readonly FieldDerivation[],
    seen = new Set<string>(),
  ): void {
    if (!derivations.length || !this.model) return
    const { items, seqs } = this.beginDerivations(derivations)
    if (!items.length) return

    this.derivationCalls.add(
      this.derivations
        .resolve(items, this.accountId)
        .subscribe(results => this.applyResults(results, seqs, seen)),
    )
  }

  /**
   * Write what a `/resolve` call answered, field by field.
   *
   *  - superseded (a newer request exists for the field) — dropped;
   *  - an error, or no result at all — the field is left as it was and marked `failed`;
   *  - `null` — the upstream had nothing to say; the field is left alone and nothing is marked;
   *  - a value — written, overwriting whatever the cell held. The derivation wins: typing a currency
   *    and then changing the instrument discards what was typed, by design.
   */
  private applyResults(
    results: readonly DerivationResult[],
    seqs: ReadonlyMap<string, number>,
    seen: Set<string>,
  ): void {
    const byKey = new Map(results.map(result => [result.keyName, result]))
    seqs.forEach((seq, name) => {
      if (this.derivationSeq.get(name) !== seq) return
      const result = byKey.get(name)
      if (!result || result.error != null) {
        console.error(
          `[transactions-2] could not derive ${name}; the field keeps its value`,
          result?.error ?? 'no result',
        )
        this.setDerivationState(name, 'failed')
        return
      }
      this.setDerivationState(name, 'idle')
      if (result.value == null) return

      this.derivedValues.set(name, result.value)
      const target = this.fieldNamed(name)
      if (!target) return
      this.write(target, result.value)
      this.settle(target)
      this.committed.set(name, committedKey(target.rawValue))
      this.deriveDependentsOf(name, seen)
    })
  }

  /**
   * Whether an EDIT holds anything to send (PVAL-26440) — the typed diff against {@link baseline},
   * never a comparison of display strings. Create and post always have something to send. A change
   * whose value will not type still counts: Save stays offered, and pressing it names the field.
   */
  get hasChanges(): boolean {
    const model = this.model
    if (!model || model.blocked) return false
    if (model.mode !== 'edit') return true
    try {
      return assembleEditRecord(model, this.baseline ?? editBaseline(model))
        .hasChanges
    } catch (error) {
      if (error instanceof SaveAssemblyError) return true
      throw error
    }
  }

  /**
   * Save's `disabled`: the Req verdict, an edit with nothing changed, a save already in flight — or
   * the read freeze. While the full record is still loading, or after its read failed, the fields on
   * screen are the grid's partial projection, and a save from them is exactly what the freeze exists
   * to prevent; the footer sits outside the inert body, so the button has to say so itself.
   */
  get saveDisabled(): boolean {
    return this.frozen || this.saveBlocked || !this.hasChanges
  }

  /** Save's tooltip — why it is disabled, when it is. */
  get saveTitle(): string | null {
    if (this.saving) return 'Saving…'
    if (this.loading) return 'Loading the transaction…'
    if (this.matchesLoading) return 'Loading matches…'
    if (this.errorLabel) return 'The transaction could not be read'
    if (this.saveBlocked) return 'A required field is incomplete'
    if (!this.hasChanges) return 'Nothing has changed'
    return null
  }

  /** Cancel and the header ✕. Refused while a save is in flight, like ESC and the backdrop. */
  dismiss(): void {
    if (this.saving) return
    this.activeModal.dismiss()
  }

  /**
   * Save — for real (PVAL-26440).
   *
   * Gated on {@link saveBlocked} — the LIVE verdict — and deliberately NOT on `vm.canSave`, which is
   * the projection's opening snapshot and would leave an enabled button that silently did nothing.
   *
   * Sends exactly ONE request, `{accountId, category?, transactions: [record]}`:
   *  - nothing at all while one is in flight, for an edit with no change, without an account, or
   *    when a value will not type (named inline instead);
   *  - 200 → the dialog closes with the created commands; the grid is not touched (the commands
   *    are pending in the drawer, not applied);
   *  - anything else → the dialog stays open and editable with the reason, and Save re-enabled. A
   *    failure that may have reached the server says to check the commands drawer before retrying.
   */
  save(): void {
    const model = this.model
    // `frozen` covers the save in flight AND the read freeze (still loading, or the read failed).
    if (!model || model.blocked || this.frozen || this.saveBlocked) return
    this.saveError = null

    const rawAccount = this.accountId.trim()
    const accountId = Number(rawAccount)
    if (!rawAccount || !Number.isInteger(accountId)) {
      this.saveError = TRANSACTION_SAVE_COPY.noAccount
      return
    }

    let record: Record<string, unknown>
    try {
      if (model.mode === 'edit') {
        if (!model.recordId) {
          this.saveError = TRANSACTION_SAVE_COPY.noRecordId
          return
        }
        const edit = assembleEditRecord(
          model,
          this.baseline ?? editBaseline(model),
        )
        if (!edit.hasChanges) return
        record = edit.record
      } else {
        record = assembleCreateRecord(model)
      }
    } catch (error) {
      if (!(error instanceof SaveAssemblyError)) throw error
      this.saveError = `${error.label} does not hold a value that can be saved. Correct it and try again.`
      return
    }

    const category = saveCategory(model.mode, model.tranCategory)
    const request: TransactionSaveRequest = {
      accountId,
      ...(category ? { category } : {}),
      transactions: [record],
    }

    this.saving = true
    this.errorHeader = undefined
    this.errorDetails = undefined
    this.saveService.save(request, model.mode).subscribe({
      next: response => {
        this.saving = false
        this.activeModal.close(response)
      },
      error: (error: unknown) => {
        this.saving = false
        const failure = saveFailureMessage(error)
        this.saveError = failure.message
        if (failure.header) {
          this.errorHeader = failure.header
          this.errorDetails = failure.details
        }
      },
    })
  }
}
